import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import woman from "../../../../assets/sellWithUs/woman.png";
import { IStage } from "../../interfaces";

export const TransparentProcessDesktopTimeLine = ({
  selectedStage,
  setSelectedStage,
  stages,
}: {
  selectedStage: number;
  setSelectedStage: React.Dispatch<React.SetStateAction<number>>;
  stages: IStage[];
}) => {
  const { i18n } = useTranslation(["sellWithUs"]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const isMid = useMediaQuery(theme.breakpoints.down(1400));
  const isRTL = i18n.dir() === "rtl";

  const timelineSliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const slider = timelineSliderRef.current;
    if (!slider || !isMobile) return;

    const handleScrollEnd = () => {
      const sliderRect = slider.getBoundingClientRect();
      const stageElements = slider.children as HTMLCollectionOf<HTMLElement>;
      let closestStage = 0;
      let minDistance = Infinity;

      // Find the stage closest to the start edge
      Array.from(stageElements).forEach((element, index) => {
        const elementRect = element.getBoundingClientRect();
        const distance = isRTL
          ? Math.abs(sliderRect.right - elementRect.right)
          : Math.abs(elementRect.left - sliderRect.left);
        if (distance < minDistance) {
          minDistance = distance;
          closestStage = index;
        }
      });

      // Override to select last stage if scrolled to the end
      const threshold = 20; // Small threshold in pixels to account for minor offsets
      if (isRTL) {
        if (
          slider.scrollLeft >=
          slider.scrollWidth - sliderRect.width - threshold
        ) {
          closestStage = stages?.length - 1;
        }
      } else {
        if (
          slider.scrollLeft + sliderRect.width >=
          slider.scrollWidth - threshold
        ) {
          closestStage = stages?.length - 1;
        }
      }

      setSelectedStage(closestStage);
    };

    slider.addEventListener("scrollend", handleScrollEnd);
    return () => slider.removeEventListener("scrollend", handleScrollEnd);
  }, [isMobile, isRTL, stages?.length]);
  return (
    <div>
      {/* // Desktop Timeline */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "flex-start",
          position: "relative",
          mb: 4,
          maxWidth: "100%",
        }}
      >
        {stages?.map((stage, index) => {
          const displayIndex = index;
          const isChecked = index <= selectedStage;

          return (
            <Box
              key={index}
              onClick={() => setSelectedStage(index)}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                position: "relative",
                textAlign: isRTL ? "right" : "left",
                zIndex: 2,
                cursor: "pointer",
                width: "100%",
              }}
            >
              <div className="transparent-desktop-title-and-num-block">
                <Typography
                  variant="h6"
                  sx={{
                    color: isChecked ? "rgba(215, 161, 72, 1)" : "#888888",
                    fontWeight: 700,
                    mb: 1,
                    fontSize: "20px",
                    fontFamily: "DM Sans",
                  }}
                >
                  {String(displayIndex + 1).padStart(2, "0")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#000000",
                    fontWeight: 500,
                    fontSize: "18px",
                    lineHeight: "22px",
                    mb: 3,
                    fontFamily: isRTL
                      ? "Noto Sans Hebrew"
                      : '"DM Sans", sans-serif',
                    width: "90%",
                  }}
                >
                  {stage?.title || "N/A"}
                </Typography>
              </div>
              <Box
                sx={{
                  position: "absolute",
                  top: isMid ? (isRTL ? "115px" : "146px") : "95px",
                  [isRTL ? "right" : "left"]: 0,
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  backgroundColor: isChecked
                    ? "rgba(215, 161, 72, 1)"
                    : "#D3D3D3",
                  zIndex: 2,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: isMid ? (isRTL ? "120px" : "150px") : "100px",
                  [isRTL ? "right" : "left"]: "5px",
                  width: "100%",
                  height: "2px",
                  backgroundColor: isChecked
                    ? "rgba(215, 161, 72, 1)"
                    : "#D3D3D3",
                  zIndex: 1,
                }}
              />
            </Box>
          );
        })}
      </Box>
      {/* Image and Description */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : isRTL ? "row-reverse" : "row",
          gap: 4,
          alignItems: "center",
          flexWrap: "wrap",
          mt: "36px",
          width: "100%",
          pt: "36px",
          minHeight: "400px",
        }}
      >
        <Box
          sx={{
            flex: isMobile ? "none" : 1,
            width: "100%",
            display: "flex",
            justifyContent: isMobile ? "center" : "flex-end",
            alignItems: "center",
            order: isRTL && !isMobile ? 1 : 2,
          }}
        >
          <img
            src={stages[selectedStage]?.image?.url || woman}
            alt={stages[selectedStage]?.title || "Stage Image"}
            style={{
              width: "auto",
              maxWidth: "100%",
              maxHeight: isMobile ? "300px" : "400px",
              objectFit: "cover",
            }}
          />
        </Box>
        <Box
          sx={{
            flex: isMobile ? "none" : 1,
            width: isMobile ? "100%" : "50%",
            order: isRTL && !isMobile ? 2 : 1,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              color: "#666",
              lineHeight: 1.6,
              fontSize: isMobile ? "0.9rem" : "18px",
              textAlign: isRTL ? "right" : "left",
              fontFamily: isRTL ? "Noto Sans Hebrew" : '"DM Sans", sans-serif',
              width: "100%",
            }}
          >
            {stages[selectedStage]?.description || "N/A"}
          </Typography>
        </Box>
      </Box>
    </div>
  );
};
